import { useState, useEffect } from 'react'
import axios from 'axios'
import { Card, CardHeader, CardTitle, CardBody, Row, Col, Spinner } from 'reactstrap'
import { Star, ThumbsUp, MessageSquare } from 'react-feather'

const BASE = process.env.REACT_APP_BASEURL
const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN)
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } })

// ─── Star Display (Read-Only) ────────────────────────────────────────────────
const StarDisplay = ({ value, size = 18 }) => {
  const num = Number(value) || 0
  return (
    <span className="d-inline-flex align-items-center" style={{ gap: '3px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          fill={n <= Math.round(num) ? '#ff9f43' : 'none'}
          stroke={n <= Math.round(num) ? '#ff9f43' : '#d0d2d6'}
        />
      ))}
      <span className="font-small-3 font-weight-bold ml-50 text-dark">
        ({num > 0 ? num.toFixed(1) : '0'}/5)
      </span>
    </span>
  )
}

const CumulativeRatings = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)

  useEffect(() => {
    let isMounted = true
    axios
      .get(`${BASE}brand/sample-feedback/overall-summary`, authHeaders())
      .then((res) => {
        if (isMounted && res.data?.success) {
          setData(res.data)
        }
      })
      .catch(() => {
        // Graceful fallback on network error or missing data
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  if (loading) {
    return (
      <Card className="shadow-none border font-inter mb-0" style={{ borderRadius: '10px' }}>
        <CardBody className="d-flex justify-content-center align-items-center py-3">
          <Spinner color="primary" size="sm" className="mr-50" />
          <span className="text-muted font-small-3">Loading overall ratings...</span>
        </CardBody>
      </Card>
    )
  }

  const avgRating = data?.avg_rating
  const recommendPct = data?.recommend_pct
  const completedCount = data?.counts?.completed ?? 0
  const ratingCount = data?.counts?.rating_count ?? completedCount
  const hasData = completedCount > 0 && (avgRating !== null || recommendPct !== null)

  return (
    <Card className="shadow-none border font-inter mb-0" style={{ borderRadius: '10px' }}>
      <CardHeader className="pb-1 border-bottom">
        <div>
          <CardTitle tag="h4" className="font-weight-bolder mb-25 text-dark" style={{ fontSize: '18px' }}>
            Overall Sample Ratings
          </CardTitle>
          <small className="text-muted">Cumulative results across all sample posts and all feedback</small>
        </div>
      </CardHeader>
      <CardBody className="pt-2 pb-1">
        {hasData ? (
          <Row>
            {/* 1. Overall Average Star Rating */}
            <Col xs={12} md={4} className="mb-1 mb-md-0">
              <div
                className="h-100 p-2 text-center rounded border"
                style={{ background: '#fafafa', borderColor: '#f1f5f9' }}
              >
                <span className="font-small-2 font-weight-bolder text-uppercase text-primary d-block mb-50">
                  Overall Average Rating
                </span>
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h2 className="font-weight-bolder text-dark mb-25" style={{ fontSize: '32px' }}>
                    {avgRating ? avgRating.toFixed(1) : '-'}
                  </h2>
                  <StarDisplay value={avgRating} size={18} />
                  <small className="text-muted font-small-2 mt-50">
                    Based on {ratingCount} dietitian {ratingCount === 1 ? 'review' : 'reviews'}
                  </small>
                </div>
              </div>
            </Col>

            {/* 2. Overall % Recommend */}
            <Col xs={12} md={4} className="mb-1 mb-md-0">
              <div
                className="h-100 p-2 text-center rounded border"
                style={{ background: '#fafafa', borderColor: '#f1f5f9' }}
              >
                <span className="font-small-2 font-weight-bolder text-uppercase text-success d-block mb-50">
                  Overall % Recommend
                </span>
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="d-flex align-items-center justify-content-center mb-25">
                    <ThumbsUp size={22} className="text-success mr-50" />
                    <h2 className="font-weight-bolder text-success mb-0" style={{ fontSize: '32px' }}>
                      {recommendPct !== null && recommendPct !== undefined ? `${recommendPct}%` : '-'}
                    </h2>
                  </div>
                  <small className="text-muted font-small-2 mt-50">
                    of dietitians recommend your products to clients
                  </small>
                </div>
              </div>
            </Col>

            {/* 3. Total Completed Feedback Responses */}
            <Col xs={12} md={4}>
              <div
                className="h-100 p-2 text-center rounded border"
                style={{ background: '#fafafa', borderColor: '#f1f5f9' }}
              >
                <span className="font-small-2 font-weight-bolder text-uppercase text-secondary d-block mb-50">
                  Total Completed Reviews
                </span>
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="d-flex align-items-center justify-content-center mb-25">
                    <MessageSquare size={22} className="text-primary mr-50" />
                    <h2 className="font-weight-bolder text-dark mb-0" style={{ fontSize: '32px' }}>
                      {completedCount}
                    </h2>
                  </div>
                  <small className="text-muted font-small-2 mt-50">
                    Dietitian feedback surveys submitted
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <div className="text-center py-2 px-1">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-1"
              style={{ width: '48px', height: '48px', background: '#f8fafc', color: '#94a3b8' }}
            >
              <Star size={24} />
            </div>
            <h5 className="font-weight-bolder text-dark mb-50">No Sample Feedback Yet</h5>
            <p className="text-muted font-small-3 mb-0" style={{ maxWidth: '480px', margin: '0 auto' }}>
              Overall average star ratings and dietitian recommendation rates will automatically appear here once dietitians complete feedback on your sample requests.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default CumulativeRatings
